import { useState } from 'react';
import { Download, Minus, Plus, RotateCcw, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

import { Button } from '@Components/ui-v2/button';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@Components/ui-v2/dialog';
import { useGeneratedFileDownload } from '@CustomHooks';
import { GeneratedFile } from '@Models';

interface ImageLightboxProps {
  open: boolean;
  onClose: () => void;
  file: GeneratedFile;
  url: string;
  conversationId: string;
  timestamp: string;
}

const ImageLightbox = ({ open, onClose, file, url, conversationId, timestamp }: ImageLightboxProps) => {
  const { t } = useTranslation();
  const { download, isPending } = useGeneratedFileDownload(conversationId, timestamp, file);
  const [zoomed, setZoomed] = useState(false);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        fullscreen
        showCloseButton={false}
        className="inset-0 flex h-[100dvh] w-screen max-w-none flex-col gap-0 rounded-none border-0 bg-black/95 p-0 text-white motion-reduce:animate-none"
        aria-describedby={undefined}>
        <TransformWrapper
          minScale={1}
          maxScale={6}
          centerOnInit
          doubleClick={{ mode: 'toggle', step: 1.5 }}
          pinch={{ step: 5 }}
          onTransform={(_, state) => setZoomed(state.scale > 1)}>
          {({ zoomIn, zoomOut, resetTransform }) => (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex items-center gap-2 px-3 py-2">
                <DialogTitle className="mr-auto truncate text-sm font-medium text-white">{file.file_name}</DialogTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('Agent.ImageZoomOut')}
                  onClick={() => zoomOut()}
                  className="h-9 w-9 cursor-pointer bg-white/10 text-white hover:bg-white/30 hover:text-white">
                  <Minus className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('Agent.ImageZoomIn')}
                  onClick={() => zoomIn()}
                  className="h-9 w-9 cursor-pointer bg-white/10 text-white hover:bg-white/30 hover:text-white">
                  <Plus className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('Agent.ImageZoomReset')}
                  onClick={() => resetTransform()}
                  disabled={!zoomed}
                  className="h-9 w-9 cursor-pointer bg-white/10 text-white hover:bg-white/30 hover:text-white">
                  <RotateCcw className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('Agent.ImageDownload')}
                  onClick={download}
                  disabled={isPending}
                  className="h-9 w-9 cursor-pointer bg-white/10 text-white hover:bg-white/30 hover:text-white">
                  <Download className="h-4 w-4" />
                </Button>
                <DialogClose asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('Agent.ImageClose')}
                    className="h-9 w-9 cursor-pointer bg-white/10 text-white hover:bg-white/30 hover:text-white">
                    <X className="h-4 w-4" />
                  </Button>
                </DialogClose>
              </div>
              <TransformComponent
                wrapperClass="!h-full !w-full flex-1 min-h-0 cursor-grab active:cursor-grabbing"
                contentClass="!h-full !w-full flex items-center justify-center">
                <img
                  src={url}
                  alt={file.file_name}
                  draggable={false}
                  className="max-h-full max-w-full select-none object-contain"
                />
              </TransformComponent>
            </div>
          )}
        </TransformWrapper>
      </DialogContent>
    </Dialog>
  );
};

export default ImageLightbox;
